/**
 * Client HTTP POP! — salle de jeu.
 *
 * Routes (relatives, same-origin : valables en local comme en production où
 * nginx proxifie `/api`) :
 *   POST /api/rooms                  { pseudo }          → CreateRoomResponse
 *   POST /api/rooms/:code/join       { pseudo }          → JoinRoomResponse
 *   GET  /api/rooms/:code                                → RoomState
 *
 * Toutes les formes sont validées avec `@pop/contracts` : aucune forme n'est
 * redéclarée ici, les types sont dérivés des schémas.
 *
 * Erreurs : on affiche le `message` / `error` renvoyé par le serveur, jamais
 * une trace technique. Une réponse inattendue ou un réseau coupé donnent un
 * message générique compréhensible.
 */
import {
  createRoomResponseSchema,
  errorSchema,
  joinRoomResponseSchema,
  roomStateSchema,
  type CreateRoomResponse,
  type JoinRoomResponse,
  type RoomState,
} from '@pop/contracts';

/**
 * Erreur destinée à être affichée telle quelle à l'utilisateur : son message
 * vient du serveur (ou d'un texte générique), jamais d'une trace technique.
 */
export class ApiRequestError extends Error {
  readonly code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
  }
}

/** Extrait le message utilisable de l'enveloppe d'erreur du serveur. */
function toApiError(payload: unknown, fallbackMessage: string): ApiRequestError {
  const parsed = errorSchema.safeParse(payload);
  if (parsed.success) {
    return new ApiRequestError(parsed.data.error, parsed.data.code);
  }
  return new ApiRequestError(fallbackMessage, 'UNKNOWN_ERROR');
}

/** Lit un corps JSON sans jamais lever : corps vide ou invalide → null. */
async function readJson(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

async function postJson(path: string, body: unknown, fallbackMessage: string): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiRequestError('serveur injoignable — vérifie ta connexion', 'NETWORK_ERROR');
  }
  const payload = await readJson(response);
  if (!response.ok) {
    throw toApiError(payload, fallbackMessage);
  }
  return payload;
}

async function getJson(path: string, fallbackMessage: string): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch(path, { method: 'GET' });
  } catch {
    throw new ApiRequestError('serveur injoignable — vérifie ta connexion', 'NETWORK_ERROR');
  }
  const payload = await readJson(response);
  if (!response.ok) {
    throw toApiError(payload, fallbackMessage);
  }
  return payload;
}

/** Crée une salle et renvoie son code, l'identifiant du joueur hôte et l'état. */
export async function createRoom(pseudo: string): Promise<CreateRoomResponse> {
  const payload = await postJson('/api/rooms', { pseudo }, 'impossible de créer la salle');
  const parsed = createRoomResponseSchema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiRequestError('réponse inattendue du serveur', 'BAD_RESPONSE');
  }
  return parsed.data;
}

/** Rejoint une salle par son code. */
export async function joinRoom(code: string, pseudo: string): Promise<JoinRoomResponse> {
  const payload = await postJson(
    `/api/rooms/${encodeURIComponent(code)}/join`,
    { pseudo },
    'impossible de rejoindre la salle',
  );
  const parsed = joinRoomResponseSchema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiRequestError('réponse inattendue du serveur', 'BAD_RESPONSE');
  }
  return parsed.data;
}

/**
 * Lit l'état courant d'une salle.
 *
 * Accepte deux enveloppes : l'état brut (`RoomState`) ou un objet
 * `{ state: RoomState }`. Tolérance volontaire : le serveur reste la source de
 * vérité et son enveloppe exacte n'est pas encore figée côté API.
 */
export async function fetchRoom(code: string): Promise<RoomState> {
  const payload = await getJson(
    `/api/rooms/${encodeURIComponent(code)}`,
    'impossible de lire la salle',
  );
  const direct = roomStateSchema.safeParse(payload);
  if (direct.success) {
    return direct.data;
  }
  if (typeof payload === 'object' && payload !== null && 'state' in payload) {
    const nested = roomStateSchema.safeParse((payload as { readonly state: unknown }).state);
    if (nested.success) {
      return nested.data;
    }
  }
  throw new ApiRequestError('réponse inattendue du serveur', 'BAD_RESPONSE');
}
